/**
 * Region Detection for LATAM vs US vs SPAIN
 */

import { detectArgentinaProvinceFromTimezone, getTemplateForArgentinaProvince } from "@/config/argentinaProvinces";
import {
  detectCountryFromLanguage,
  detectCountryFromTimezone,
  getTemplateForCountry,
  type KnownRuntimeTemplate,
} from "@/config/countryTemplates";

export type Region = "LATAM" | "US" | "SPAIN" | "OTHER";

// LATAM countries
const LATAM_COUNTRIES = [
  "AR", // Argentina
  "BO", // Bolivia
  "BR", // Brazil
  "CL", // Chile
  "CO", // Colombia
  "CR", // Costa Rica
  "CU", // Cuba
  "DO", // Dominican Republic
  "EC", // Ecuador
  "SV", // El Salvador
  "GT", // Guatemala
  "HN", // Honduras
  "MX", // Mexico
  "NI", // Nicaragua
  "PA", // Panama
  "PY", // Paraguay
  "PE", // Peru
  "PR", // Puerto Rico
  "UY", // Uruguay
  "VE", // Venezuela
];

/**
 * Detect user region based on browser locale and timezone
 */
export function detectRegion(): Region {
  if (typeof window === "undefined") return "LATAM"; // Default

  // Try to detect from timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // US timezones
  const US_TIMEZONES = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Phoenix",
    "America/Anchorage",
    "America/Adak",
    "Pacific/Honolulu",
  ];

  if (US_TIMEZONES.includes(timezone)) {
    return "US";
  }

  // Spain timezones
  const SPAIN_TIMEZONES = [
    "Europe/Madrid",
    "Europe/Barcelona", // Same as Madrid but sometimes used
    "Atlantic/Canary",  // Canary Islands
  ];

  if (SPAIN_TIMEZONES.includes(timezone)) {
    return "SPAIN";
  }

  // LATAM timezones
  const LATAM_TIMEZONES = [
    "America/Argentina",
    "America/Sao_Paulo",
    "America/Mexico_City",
    "America/Bogota",
    "America/Lima",
    "America/Santiago",
    "America/Caracas",
    "America/Montevideo",
    "America/Asuncion",
    "America/La_Paz",
    "America/Guayaquil",
    "America/Havana",
    "America/Santo_Domingo",
    "America/Guatemala",
    "America/Tegucigalpa",
    "America/Managua",
    "America/San_Jose",
    "America/Panama",
  ];

  if (LATAM_TIMEZONES.some(tz => timezone.includes(tz.split("/")[1]))) {
    return "LATAM";
  }

  // Try to detect from navigator language
  const browserLang = navigator.language || (navigator as unknown as { userLanguage: string }).userLanguage;
  const countryCode = browserLang.split("-")[1]?.toUpperCase();

  if (countryCode === "US") {
    return "US";
  }

  if (countryCode === "ES") {
    return "SPAIN";
  }

  if (countryCode && LATAM_COUNTRIES.includes(countryCode)) {
    return "LATAM";
  }

  // Default to LATAM (since current site is in Spanish)
  return "LATAM";
}

/**
 * Get region-specific configuration
 */
export function getRegionConfig(region: Region) {
  // Try to detect more specific country code from browser
  const browserLang = typeof window !== "undefined" ? 
    (navigator.language || (navigator as unknown as { userLanguage: string }).userLanguage) : "";
  const countryCode = browserLang.split("-")[1]?.toUpperCase();
  
  return {
    LATAM: {
      defaultLanguage: countryCode && LATAM_COUNTRIES.includes(countryCode) 
        ? `es-${countryCode}` 
        : "es-MX", // Default to Mexico Spanish
      currency: "USD", // or ARS, MXN, etc.
      dateFormat: "DD/MM/YYYY",
    },
    US: {
      defaultLanguage: "en-US",
      currency: "USD",
      dateFormat: "MM/DD/YYYY",
    },
    SPAIN: {
      defaultLanguage: "es-ES",
      currency: "EUR",
      dateFormat: "DD/MM/YYYY",
    },
    OTHER: {
      defaultLanguage: "en-US",
      currency: "USD",
      dateFormat: "MM/DD/YYYY",
    },
  }[region];
}

/**
 * Get template type for current user's country (LATAM specific)
 * Returns the template that should be used based on country-specific configuration
 */
export function getTemplateForCurrentCountry(): KnownRuntimeTemplate | null {
  if (typeof window === "undefined") return null;

  // Check for test country first (development)
  const testCountry = localStorage.getItem("test_country");
  if (testCountry && import.meta.env.DEV) {
    return getTemplateForCountry(testCountry);
  }

  // Try to detect country from timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const countryFromTimezone = detectCountryFromTimezone(timezone);
  
  if (countryFromTimezone) {
    return getTemplateForCountry(countryFromTimezone);
  }

  // Try to detect from browser language
  const browserLang = navigator.language || (navigator as unknown as { userLanguage: string }).userLanguage;
  const countryFromLanguage = detectCountryFromLanguage(browserLang);
  
  if (countryFromLanguage) {
    return getTemplateForCountry(countryFromLanguage);
  }

  // Return null if no specific country configuration found
  return null;
}

/**
 * Get template type for current user if they're in Argentina (province-specific)
 * Returns the template that should be used based on Argentine province configuration
 */
export function getTemplateForArgentinaUser(): "BaseTemplate" | "BioLinkTemplate" | null {
  if (typeof window === "undefined") return null;

  // Check for test province first (development)
  const testProvince = localStorage.getItem("test_argentina_province");
  if (testProvince && import.meta.env.DEV) {
    return getTemplateForArgentinaProvince(testProvince);
  }

  // Try to detect province from timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const provinceFromTimezone = detectArgentinaProvinceFromTimezone(timezone);
  
  if (provinceFromTimezone) {
    return getTemplateForArgentinaProvince(provinceFromTimezone);
  }

  // If we can't detect specific province but we know it's Argentina, 
  // default to BaseTemplate (more accessible for unknown provinces)
  const countryFromTimezone = detectCountryFromTimezone(timezone);
  if (countryFromTimezone === "AR") {
    return "BaseTemplate";
  }

  // Return null if not in Argentina
  return null;
}
